import React, { FC, useEffect, useState } from 'react'
import { OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import {
  getPlatformProductAdjustmentSecondaryAsync,
  getPlatformProductDetailAsync,
  getPlatformProductVarientDetailAsync,
} from 'src/redux/actions/productActions'
import { getActiveBundleListAsync } from 'src/redux/actions/activeBundleAction'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import Loader from 'src/utils/Loader'
import { Link } from 'react-router-dom'
import StatusButton from '../orders/StatusButton'
import ImageGallery from './ImageGallery'
import { VARIANT_DATA } from 'src/interfaces/Platform.interface'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { formatDateLocal, formatTimeZone } from 'src/utils/TimeZoneDateFormat'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { Card, CardContent, Tabs, Tab, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import InventoryLogs from '../inventory/InventoryLogs'
import PriceLogs from '../price-logs/PriceLogs'
import { getInventoryDetailsAsync } from 'src/redux/actions/omsProductActions'
import PremiumBreadcrumb from 'src/app/components/navigation/PremiumBreadcrumb'

const CommonColumn: FC<{ rowData: string }> = ({ rowData }) => {
  return (
    <>
      <p className='mt-2'>{rowData}</p>
    </>
  )
}
const StyledTabs = styled(Tabs)(({ theme }) => ({
  "& .MuiTabs-indicator": {
    backgroundColor: "#007BFF", // bright blue underline
    height: 3,
    borderRadius: 2,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "15px",
  textTransform: "none",
  color: "#007BFF",
  "&.Mui-selected": {
    color: "#007BFF",
  },
  "&:hover": {
    color: "#0056b3",
  },
  minHeight: 48,
  marginRight: theme.spacing(2),
}));

const formatLinkedTypeLabel = (type?: string): string => {
  const normalized = `${type ?? ''}`.trim().toLowerCase()
  if (!normalized) return 'Channel'
  if (normalized === 'erp') return 'ERP'
  if (normalized === 'oms') return 'OMS'
  if (normalized === 'primary') return 'Source'
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

const extractBundleRelationDataFromProductDetail = (productDetail: any): any[] => {
  const fullBundleRelationData = Array.isArray(productDetail?.varients_data_relation)
    ? productDetail.varients_data_relation
    : []

  return fullBundleRelationData.flatMap((relation: any) => {
    const relationVariant = relation?.platform_product_variant || relation
    if (!relationVariant) return []

    const relationBundleData = Array.isArray(relationVariant?.bundle_relation_data)
      ? relationVariant.bundle_relation_data
      : Array.isArray(relation?.bundle_relation_data)
      ? relation.bundle_relation_data
      : []

    return relationBundleData.length > 0 ? relationBundleData : []
  })
}

const ProductDetail: FC = () => {
  const params = useParams();
  const { variantId, platformId, productId, availablePlatformId } = params
  const [paginationData, setPaginationData] = useState({
    page: 1,
    limit: 10,
    sort_by: 'api_created_at',
    sort_order: 'DESC',
  })

  const {
    platformProductDetailLoader,
    platformProductDetail,
    platformAdjustmentCount,
    platformAdjustments,
    platformAdjustmentLoader,
  } = useAppSelector((state) => state.productsOfSingleChannel)

  const { mainStoreData, fetchedConnectedPlatformData } = useAppSelector((state) => state.fetchConnectedPlatform)
  const dispatch = useAppDispatch()
  const [selectedProduct, setSelectedProduct] = useState<any>({})
  const [activeTab, setActiveTab] = useState(0);
  const isProductDetailPage = true;
  const [warehouseDetails, setWarehouseDetails] = useState<any[]>([])
  const [warehouseDetailsLoader, setWarehouseDetailsLoader] = useState(false)
  const [inventorySummary, setInventorySummary] = useState<any>(null)
  const [showWarehouseTab, setShowWarehouseTab] = useState(false)
  const [resolvedBundleRelationData, setResolvedBundleRelationData] = useState<any[]>([])

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  console.log(selectedProduct, 'selectedProductselectedProduct')

  const currentPlatformName = ((platform: any) => {
    return (
      platform?.platform_info?.display_name ??
      platform?.display_name ??
      platform?.name ??
      'Products'
    )
  })(
    fetchedConnectedPlatformData?.find(
      (p: any) => p?.id === Number(params?.platformId)
    )
  )

  useEffect(() => {
    if (variantId && platformProductDetail?.varients_data) {
      const variant = platformProductDetail.varients_data.find(
        (item: any) => item.id === Number(variantId)
      )

      setSelectedProduct(variant || {})
    } else {
      setSelectedProduct({})
    }
  }, [platformProductDetail, variantId, dispatch]);

  useEffect(() => {
    if (productId) {
      dispatch(getPlatformProductDetailAsync({ productId }))
    }
  }, [productId, dispatch])

  useEffect(() => {
    let cancelled = false

    const resolveBundleRelationsFromLinkedChannel = async () => {
      const isMainStoreView =
        mainStoreData?.id && Number(platformId) === Number(mainStoreData.id)

      if (!isMainStoreView || !selectedProduct?.sku) {
        setResolvedBundleRelationData([])
        return
      }

      const existingBundleRelationData =
        extractBundleRelationDataFromProductDetail(platformProductDetail)
      const selectedVariantBundleData = Array.isArray(selectedProduct?.bundle_relation_data)
        ? selectedProduct.bundle_relation_data
        : []

      if (existingBundleRelationData.length > 0 || selectedVariantBundleData.length > 0) {
        setResolvedBundleRelationData([])
        return
      }

      const searchSku = `${selectedProduct.sku}`.trim()
      if (!searchSku) {
        setResolvedBundleRelationData([])
        return
      }

      const platformsToSearch = (fetchedConnectedPlatformData || []).filter(
        (platform: any) => Number(platform?.id) !== Number(mainStoreData?.id)
      )

      for (const platform of platformsToSearch) {
        try {
          const bundleResponse: any = await dispatch(
            getActiveBundleListAsync({
              paramData: {
                platform_id: Number(platform.id),
                page: 1,
                limit: 10,
                search: searchSku,
              },
            })
          )

          if (cancelled) return

          const bundles = bundleResponse?.payload?.data?.data ?? []
          const matchedBundle = bundles.find(
            (bundle: any) =>
              `${bundle?.sku ?? ''}`.trim().toLowerCase() === searchSku.toLowerCase()
          )

          if (!matchedBundle?.platform_product_variant_id) continue

          const variantResponse: any = await dispatch(
            getPlatformProductVarientDetailAsync({
              productVarientId: String(matchedBundle.platform_product_variant_id),
            })
          )

          if (cancelled) return

          const linkedVariant = variantResponse?.payload?.varients_data?.find(
            (variant: any) =>
              Number(variant?.id) === Number(matchedBundle.platform_product_variant_id)
          )
          const bundleRelationData = linkedVariant?.bundle_relation_data

          if (Array.isArray(bundleRelationData) && bundleRelationData.length > 0) {
            setResolvedBundleRelationData(bundleRelationData)
            return
          }
        } catch {
          // Try the next connected platform.
        }
      }

      if (!cancelled) {
        setResolvedBundleRelationData([])
      }
    }

    resolveBundleRelationsFromLinkedChannel()

    return () => {
      cancelled = true
    }
  }, [
    selectedProduct?.sku,
    selectedProduct?.bundle_relation_data,
    platformId,
    platformProductDetail,
    mainStoreData?.id,
    fetchedConnectedPlatformData,
    dispatch,
  ])

  useEffect(() => {
    if (platformId && variantId && productId) {
      dispatch(
        getPlatformProductAdjustmentSecondaryAsync({
          platformId,
          otherParams: {
            ...paginationData,
            variant_id: variantId,
            product_id: productId,
          },
        })
      )
    }
  }, [paginationData, platformId, variantId, productId, dispatch])

  useEffect(() => {
    const fetchInventoryDetails = async () => {
      if (!selectedProduct?.id) return
      const inventoryPlatformId = platformId ?? platformProductDetail?.platform

      try {
        setWarehouseDetailsLoader(true)

        const response: any = await dispatch(
          getInventoryDetailsAsync({
            page: 1,
            limit: 10,
            sort_by: "desc",
            order_by: "id",
            variant_ids: String(selectedProduct?.id),
            platform_id: Number(inventoryPlatformId)
          })
        )

        const inventoryList = response?.payload?.results?.data || []
        const inventoryData = inventoryList?.[0] || null

        // show warehouse tab only when results.data is not empty
        setShowWarehouseTab(Array.isArray(inventoryList) && inventoryList.length > 0)

        setInventorySummary(inventoryData)
        setWarehouseDetails(inventoryData?.locations || [])
      } catch (error) {
        setShowWarehouseTab(false)
        setInventorySummary(null)
        setWarehouseDetails([])
      } finally {
        setWarehouseDetailsLoader(false)
      }
    }

    fetchInventoryDetails()
  }, [selectedProduct?.id, platformId, platformProductDetail?.platform, dispatch])

  if (platformProductDetailLoader || platformAdjustmentLoader) return <Loader />

  const variants_table_header_column = [
    {
      name: 'Variant Name',
      cell: (row) => (
        <CommonColumn
          rowData={
            row?.platforminventoryadjustmentsyncdata_set[0]?.updated_product_variant?.name == 'Default Title'
              ? row?.platforminventoryadjustmentsyncdata_set[0]?.updated_product_variant?.platform_product?.name
              : row?.platforminventoryadjustmentsyncdata_set[0]?.updated_product_variant?.platform_product?.name
          }
        />
      ),
      sortable: false,
      grow: 2,
    },
    {
      name: 'Available Quantity',
      cell: (row) => <p className='mb-1'>{row?.platforminventoryadjustmentsyncdata_set[0]?.updated_quantity}</p>,

      sortable: false,
      right: true,
      center: true,
    },
    {
      name: (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
            Created At{' '}
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
            >
              <span style={{ cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
            </OverlayTrigger>
          </div>
        </>
      ),
      cell: (row) => {
        return (
          <CommonColumn
            rowData={
              row?.api_created_at
                ? formatDateLocal(row.api_created_at)
                : 'N/A'
            }
          />
        );
      },
      right: true,
      width: '160px',
      center: true,
      sortField: 'api_created_at',
    },
    {
      name: 'Status',
      cell: (row) =>
        <div className='d-flex ms-2 w-75'>
          <StatusButton
            syncStatus={row?.platforminventoryadjustmentsyncdata_set[0]?.destination_sync_status}
            syncMessage={row?.platforminventoryadjustmentsyncdata_set[0]?.api_message}
          />
        </div>,
      sortable: true,
      sortField: 'Status',
      center: true,
    },
  ]

  const handleSort = (ev, sortDirection) => {
    setPaginationData((pre) => {
      return { ...pre, sort_by: ev.sortField, sort_order: (sortDirection + '').toUpperCase() }
    })
  }

  const product_variant_detail = platformProductDetail?.varients_data.length &&
    platformProductDetail?.varients_data.find(
      (item: VARIANT_DATA) => item?.id === Number(variantId)
    )

  const getConnectedPlatformDisplayName = (linkedItem: any): string => {
    const linkedPlatformId = linkedItem?.platform_product?.platform
    const matchedPlatform = fetchedConnectedPlatformData?.find(
      (platform: any) => Number(platform?.id) === Number(linkedPlatformId)
    )

    return (
      matchedPlatform?.display_name ||
      matchedPlatform?.platform_info?.display_name ||
      linkedItem?.platform_product?.display_name ||
      linkedItem?.platform_product?.name ||
      ''
    )
  }

  const getLinkedAvailablePlatformId = (linkedItem: any): string => {
    const linkedPlatformId = linkedItem?.platform_product?.platform
    const matchedPlatform = fetchedConnectedPlatformData?.find(
      (platform: any) => Number(platform?.id) === Number(linkedPlatformId)
    )

    const resolvedAvailablePlatformId =
      linkedItem?.platform_product?.available_platform_id ??
      linkedItem?.available_platform_id ??
      matchedPlatform?.platform_info?.id ??
      availablePlatformId

    return resolvedAvailablePlatformId ? String(resolvedAvailablePlatformId) : ''
  }

  const getLinkedCardTitle = (linkedItem: any, linkedVariants: any[] = []): string => {
    const typeLabel = formatLinkedTypeLabel(linkedItem?.type)
    const platformName = getConnectedPlatformDisplayName(linkedItem)
    const relationLabel = platformName ? `${typeLabel} - ${platformName}` : typeLabel

    if (typeLabel === 'Source' && linkedVariants.length > 0) {
      return `Linked to ${relationLabel}'s Bundle Child Items`
    }

    return `Linked to ${relationLabel}`
  }

  const getBundleRelationVariants = (linkedItem: any) => {
    if (formatLinkedTypeLabel(linkedItem?.type) !== 'Source') {
      return []
    }

    const productDetail: any = platformProductDetail
    const fullBundleRelationData = Array.isArray(productDetail?.varients_data_relation)
      ? productDetail.varients_data_relation
      : []
    const selectedVariantId = Number(selectedProduct?.id ?? variantId)
    const productLevelBundleRelationData =
      fullBundleRelationData.find((relation: any) => {
        const relationVariant = relation?.platform_product_variant || relation
        return Number(relationVariant?.id) === selectedVariantId
      })?.bundle_relation_data || []

    const bundleRelationSources = [
      linkedItem?.bundle_relation_data,
      selectedProduct?.bundle_relation_data,
      productLevelBundleRelationData,
      resolvedBundleRelationData,
    ]
    const bundleRelationData =
      bundleRelationSources.find((source) => Array.isArray(source) && source.length > 0) || []

    return bundleRelationData
      .map((relation: any) => relation?.platform_product_variant || relation)
      .filter(Boolean)
  }

  const hasBundleRelationVariants = (linkedItem: any) =>
    getBundleRelationVariants(linkedItem).length > 0

  const getOrderedParentDetails = () => {
    const productVariantDetail: any = product_variant_detail
    const parentDetails = Array.isArray(productVariantDetail?.parent_details)
      ? productVariantDetail.parent_details
      : []
    const productDetail: any = platformProductDetail
    const fullBundleRelationVariants =
      extractBundleRelationDataFromProductDetail(productDetail)
    const effectiveFullBundleRelationVariants =
      fullBundleRelationVariants.length > 0
        ? fullBundleRelationVariants
        : resolvedBundleRelationData

    if (parentDetails.length === 0 && effectiveFullBundleRelationVariants.length > 0) {
      return [
        {
          item: {
            ...selectedProduct,
            type: 'Primary',
            isFullBundleRelationView: true,
            bundle_relation_data: effectiveFullBundleRelationVariants,
          },
          index: 0,
        },
      ]
    }

    return parentDetails
      .filter((item: any) => {
        if (mainStoreData?.id === Number(platformId)) {
          return item.type !== 'Primary'
        }
        return true
      })
      .map((item: any, index: number) => ({ item, index }))
      .sort((first: any, second: any) => {
        const firstHasBundle = hasBundleRelationVariants(first.item)
        const secondHasBundle = hasBundleRelationVariants(second.item)

        if (firstHasBundle !== secondHasBundle) {
          return firstHasBundle ? 1 : -1
        }

        return first.index - second.index
      })
  }

  return (
    <>
      <div
        id='kt_page_title'
        data-kt-swapper='true'
        data-kt-swapper-mode='prepend'
        data-kt-swapper-parent="{default: '#kt_content_container', 'lg': '#kt_toolbar_container'}"
        className='page-title d-flex flex-wrap me-3 flex-column justify-content-center mb-4'
      >
        <div className='d-flex flex-column'>
          <PremiumBreadcrumb
            items={[
              {
                label: currentPlatformName,
                to: `/listing-product-table/${availablePlatformId}/${platformId}`,
              },
              { label: 'Product Details' },
            ]}
          />
          <h1 className='page-heading d-flex text-gray-900 fw-bold fs-3 my-0 flex-column justify-content-center'>
            General Info
          </h1>
        </div>
      </div>

      <div className='tab-content' id='myTabContent'>
        <div className='tab-pane fade show active' id='kt_tab_pane_4' role='tabpanel'>
          <div className='card'>
            <div className='p-4'>
              <div className='border border-bottom border-top-0 border-start-0 border-end-0 py-2'>
                <label htmlFor='' className='fs-5 text-sec ondary fw-bold'>
                  Product Details
                </label>
              </div>
              <div className='py-4'>
                {/* <div className='card'>
                  <div className='border border-bottom p-2'>
                    <p className='h5 fw-bold'>Product Name</p>
                  </div>
                </div> */}
                <div className='align-items-center mt-2 p-3'>
                  <div><span className='fs-5 fw-medium d-inline-block' style={{ minWidth: '5.4rem' }}>Name</span> :<span className='h5 ms-2 mb-0 text-info text-preserve-spacing'>{platformProductDetail?.name}</span></div>
                  {platformProductDetail?.name !== selectedProduct?.name && <div><span className='fs-5 fw-medium d-inline-block' style={{ minWidth: '5.4rem' }}>Variant</span> :<span className='h5 ms-2 mb-0 text-secondary text-preserve-spacing'>{selectedProduct?.name}</span></div>}
                  {selectedProduct?.sku && <div><span className='fs-5 fw-medium d-inline-block' style={{ minWidth: '5.4rem' }}>SKU</span> :<span className='h5 ms-2 mb-0 text-secondary'>{selectedProduct?.sku}</span></div>}
                  {/* {linkedProduct?.name && <div><span className='fs-5 fw-medium d-inline-block' style={{minWidth: '5rem'}}>Linked To</span> :<span className='h5 ms-2 mb-0 text-secondary'>{linkedProduct?.name}</span></div>}
                  {linkedProduct?.sku &&  <span className='fs-5 fw-medium text-secondary' style={{marginLeft: '6.5rem'}}>SKU - {linkedProduct?.sku} </span>}
                  {linkedProduct?.barcode &&  <span className='fs-5 fw-medium text-secondary' style={{marginLeft: '6.5rem'}}>Barcode - {linkedProduct?.barcode} </span>} */}
                </div>
                <div className="mt-2 p-4">

                  <div className="row mt-3">
                    {product_variant_detail ? (
                      getOrderedParentDetails().map(({ item, index }: any) => (
                        <div
                          className={`${
                            item?.isFullBundleRelationView ? 'col-md-8 col-lg-6' : 'col-md-4'
                          } mb-4`}
                          key={`${item?.type || 'linked'}-${item?.id || index}`}
                        >
                          {(() => {
                            const linkedVariants = getBundleRelationVariants(item)

                            return (
                              <div className="position-relative mt-4">
                                <div
                                  className="position-absolute top-0 start-50 translate-middle bg-white px-3 text-secondary fw-bold"
                                  style={{
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: 1,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {getLinkedCardTitle(item, linkedVariants)}
                                </div>

                                <div className="card shadow-sm border-secondary pt-3">
                                  <div className=" ps-3 py-2 fs-5 fw-medium" style={{ color: 'dimgray' }}  >
                                    {linkedVariants.length > 0 ? (
                                      <>
                                        {linkedVariants.map((linkedVariant: any, linkedIndex: number) => {
                                          const linkedAvailablePlatformId = getLinkedAvailablePlatformId(linkedVariant)

                                          return (
                                            <div
                                              key={linkedVariant?.id || `${item?.id}-${linkedIndex}`}
                                              className={
                                                linkedIndex !== linkedVariants.length - 1
                                                  ? 'mb-4 pb-2 border-bottom'
                                                  : 'mb-2'
                                              }
                                            >
                                              <div className="mb-2">
                                                <span>
                                                  {linkedVariants.length > 1
                                                    ? `Child Item Name ${linkedIndex + 1}`
                                                    : 'Name'}{' '}
                                                  :
                                                </span>{' '}
                                                <span className='' style={{ color: 'gray' }}>
                                                  {linkedVariant?.name ||
                                                    linkedVariant?.platform_product?.name ||
                                                    'N/A'}
                                                </span>
                                              </div>
                                              <div className="mb-2">
                                                <span>SKU &nbsp;&nbsp; :</span>{' '}
                                                {linkedVariant?.id &&
                                                  linkedVariant?.platform_product?.platform &&
                                                  linkedVariant?.platform_product?.id &&
                                                  linkedAvailablePlatformId ? (
                                                  <Link
                                                    to={`/product-variant-detail/${linkedVariant.id}/platform/${linkedVariant.platform_product.platform}/product/${linkedVariant.platform_product.id}/${linkedAvailablePlatformId}`}
                                                    className='text-info text-decoration-none'
                                                  >
                                                    {linkedVariant?.sku || 'N/A'}
                                                  </Link>
                                                ) : (
                                                  <span className='' style={{ color: 'gray' }}>
                                                    {linkedVariant?.sku || 'N/A'}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          )
                                        })}

                                        {linkedVariants.length > 0 && !item?.isFullBundleRelationView && (
                                            <div className="mb-2">
                                              <span>See Full Bundle relation details: </span>
                                              <Link
                                                to={`/bundles?platform_id=${platformId}`}
                                                className='text-info text-decoration-none'
                                              >
                                                Here
                                              </Link>
                                            </div>
                                        )}
                                      </>
                                    ) : (
                                      (() => {
                                        const linkedAvailablePlatformId = getLinkedAvailablePlatformId(item)

                                        return (
                                          <>
                                            <div className="mb-2">
                                              <span>Name :</span>{' '}
                                              <span className='' style={{ color: 'gray' }}>{item.name || 'N/A'}</span>
                                            </div>
                                            <div className="mb-2">
                                              <span>SKU &nbsp;&nbsp; :</span>{' '}
                                              {Array.isArray(platformProductDetail?.varients_data) &&
                                                platformProductDetail.varients_data.length > 0 &&
                                                item?.id &&
                                                item?.platform_product?.platform &&
                                                item?.platform_product?.id &&
                                                linkedAvailablePlatformId ? (
                                                <Link
                                                  to={`/product-variant-detail/${item.id}/platform/${item.platform_product.platform}/product/${item.platform_product.id}/${linkedAvailablePlatformId}`}
                                                  className='text-info text-decoration-none'
                                                >
                                                  {item.sku || 'N/A'}
                                                </Link>
                                              ) : (
                                                <span className='' style={{ color: 'gray' }}>{item.sku || 'N/A'}</span>
                                              )}
                                            </div>
                                          </>
                                        )
                                      })()
                                    )}
                                  </div>
                                </div>
                              </div>
                            )
                          })()}
                        </div>
                      ))
                    ) : (
                      <p>Variant not found</p>
                    )}
                  </div>
                </div>

                <ImageGallery platformProductDetail={platformProductDetail} />
              </div>
            </div>
          </div>



          <Box sx={{ width: "100%", mt: 3 }}>
            <Card
              sx={{
                borderRadius: "8px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                border: "1px solid #ddd",
              }}
            >
              <Box sx={{ borderBottom: "1px solid #e0e0e0", px: 2, pt: 1 }}>
                <StyledTabs value={activeTab} onChange={handleChange}>
                  {showWarehouseTab && <StyledTab label="Warehouse Details" />}
                  <StyledTab label="Inventory Logs" />
                  <StyledTab label="Price Logs" />
                </StyledTabs>
              </Box>

              <CardContent sx={{ p: 2 }}>
                {showWarehouseTab ? (
                  <>
                    {activeTab === 0 && (
                      <>
                        <h3 style={{ color: '#000', fontWeight: 600, marginBottom: '13px', marginLeft: '10px', fontSize: '14px' }}
                        >Inventory Breakdown by Warehouse</h3>
                        <div
                          className='border rounded'
                          style={{
                            height: '340px',
                            overflow: 'hidden',
                            borderColor: '#e5e7eb',
                            backgroundColor: '#fff',
                          }}
                        >
                          {/* Header */}
                          <div
                            style={{
                              background: '#f8f9fa',
                              borderBottom: '1px solid #e5e7eb',
                            }}
                          >
                            <div className='row g-0 px-4 py-3'>
                              <div className='col-8'>
                                <span className='fw-bold fs-6 text-dark'>Warehouse Name</span>
                              </div>
                              <div className='col-4' style={{ textAlign: "center" }}>
                                <span className='fw-bold fs-6 text-dark'>Current Available</span>
                              </div>
                            </div>
                          </div>

                          {/* Body */}
                          <div
                            style={{
                              height: 'calc(360px - 58px - 56px)',
                              overflowY: 'auto',
                            }}
                          >
                            {warehouseDetailsLoader ? (
                              <div className='d-flex justify-content-center align-items-center h-100'>
                                <Spinner animation='border' size='sm' />
                              </div>
                            ) : warehouseDetails?.length > 0 ? (
                              warehouseDetails.map((item: any, index: number) => (
                                <div
                                  key={item?.location_id || index}
                                  className='row g-0 px-4 py-3'
                                  style={{
                                    borderBottom:
                                      index !== warehouseDetails.length - 1 ? '1px solid #e9ecef' : 'none',
                                  }}
                                >
                                  <div className='col-8'>
                                    <span className='fs-6 text-gray-800'>
                                      {item?.location_name || 'N/A'}
                                    </span>
                                  </div>
                                  <div className='col-4' style={{ textAlign: "center" }}>
                                    <span className='fs-6 text-gray-800'>
                                      {item?.available_quantity ?? 0}
                                    </span>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className='d-flex justify-content-center align-items-center h-100 text-muted fs-6'>
                                No warehouse details found
                              </div>
                            )}
                          </div>

                          {/* Footer */}
                          <div
                            style={{
                              height: '46px',
                              background: '#f8f9fa',
                              borderTop: '1px solid #e5e7eb',
                            }}
                            className='row g-0 px-3 align-items-center'
                          >
                            <div className='col-8'>
                              <span className='fw-bold fs-6 text-dark'>Total Available Quantity</span>
                            </div>
                            <div className='col-4' style={{ textAlign: "center" }}>
                              <span className='fw-bold fs-6 text-dark'>
                                {inventorySummary?.total_available_quantity ?? 0}
                              </span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {activeTab === 1 && (
                      <InventoryLogs isProductDetailPage={isProductDetailPage} variantId={selectedProduct?.id} />
                    )}

                    {activeTab === 2 && (
                      <PriceLogs isProductDetailPage={isProductDetailPage} variantId={selectedProduct?.id} />
                    )}
                  </>
                ) : (
                  <>
                    {activeTab === 0 && (
                      <InventoryLogs isProductDetailPage={isProductDetailPage} variantId={selectedProduct?.id} />
                    )}
                    {activeTab === 1 && (
                      <PriceLogs isProductDetailPage={isProductDetailPage} variantId={selectedProduct?.id} />
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        </div>
      </div>
    </>
  )
}

export default ProductDetail
